<template>
  <modal
    v-model="show"
    class="d-flex justify-content-center align-items-center"
    content-class="theme-modal-content overflow-hidden"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    :click-to-close="true"
    :esc-to-close="true"
  >
    <div
      class="theme-modal-container"
      ref="draggableContainer"
      @mousedown="dragMouseDown"
    >
      <div class="modal-title border-bottom position-relative bg-body">
        <p class="p-2 m-0">System Theme Colors</p>
        <button class="close mr-3 pr-2" @click="closeModal">&times;</button>
      </div>
      <div class="content-container">
        <div
          v-if="step === 'grid'"
          class="
            colors-grid
            d-flex
            flex-wrap
            justify-content-center
            align-items-start
            py-5
          "
        >
          <div
            v-for="item in availableColors"
            :key="item.id"
            class="
              colors-item
              d-flex
              align-items-center
              justify-content-center
              px-3
              py-2
            "
            :class="isSelected(item) ? 'selected' : ''"
            role="button"
            tabindex="0"
            :aria-pressed="isSelected(item)"
            :aria-label="themeLabel(item)"
            :title="item.name || null"
            @click="selectTheme(item)"
            @keydown.enter.prevent="selectTheme(item)"
            @keydown.space.prevent="selectTheme(item)"
          >
            <theme-swatch :theme="item" :spectrum="isUnsetCustom(item)" />
            <span v-if="item.id === 'custom'" class="custom-caption">
              <i class="fas fa-pencil-alt"></i>
              <span>Custom</span>
            </span>
            <span v-else-if="item.name" class="theme-caption">{{
              item.name
            }}</span>
          </div>
        </div>

        <div v-else class="picker-step pt-3 pb-4">
          <button
            type="button"
            class="btn btn-link back-link p-0 mb-3"
            @click="backToThemes"
          >
            <i class="fas fa-chevron-left mr-1"></i>
            Back to themes
          </button>

          <!-- .stop: dragging a colour must not drag the modal. -->
          <div
            class="
              d-flex
              flex-wrap
              justify-content-center justify-content-md-start
            "
            @mousedown.stop
          >
            <div class="picker-holder">
              <ColorPicker
                :color="pickerColor"
                :onStartChange="onPickerChange"
                :onChange="onPickerChange"
                :onEndChange="onPickerChange"
              />
            </div>

            <div class="preview-holder ml-4 d-none d-md-block">
              <theme-preview :theme-color="customTheme.themeColor" />
            </div>
          </div>

          <div v-if="isAdmin" class="clinic-theme-save mt-3" @mousedown.stop>
            <p v-if="clinicThemesFull" class="text-muted small mb-0">
              The clinic theme limit is reached. Delete a clinic theme to add
              another.
            </p>
            <template v-else>
              <alert class="mb-3" color="info" :hideLoader="true">
                Want to try it first? Click Apply and browse the app with this
                colour. Come back any time, open Custom, and save it as a clinic
                theme.
              </alert>
              <label class="small mb-1" for="clinic-theme-name">Name</label>
              <div class="clinic-theme-save-row">
                <input
                  id="clinic-theme-name"
                  v-model="clinicThemeName"
                  class="form-control form-control-sm"
                  type="text"
                  placeholder="e.g. Canada Basketball Red"
                  :aria-invalid="!!clinicThemeError"
                  aria-describedby="clinic-theme-error"
                  @keyup.enter="saveAsClinicTheme"
                />
                <button
                  type="button"
                  class="btn btn-theme btn-sm"
                  :disabled="!canSaveAsClinicTheme"
                  @click="saveAsClinicTheme"
                >
                  {{
                    savingClinicTheme
                      ? "Saving..."
                      : "Apply and save as clinic theme"
                  }}
                </button>
              </div>
              <small
                v-if="clinicThemeError"
                id="clinic-theme-error"
                class="text-danger d-block mt-1"
                role="alert"
              >
                {{ clinicThemeError }}
              </small>
            </template>
            <router-link
              :to="{ name: 'themes.settings' }"
              class="manage-link d-block small mt-2"
              @click="closeModal"
            >
              Manage clinic themes
            </router-link>
          </div>
        </div>
      </div>

      <div class="p-4 d-flex justify-content-between border-top border-top-2">
        <button class="btn btn-light col-5" @click="closeModal">Cancel</button>
        <button
          class="btn btn-theme col-5"
          :disabled="!canApply"
          @click="storeSelectedTheme"
        >
          Apply
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import { debounce } from "lodash";
import Swal from "sweetalert2";
import { mapActions, mapState } from "vuex";
import { ColorPicker } from "vue3-color-gradient-picker";
import ThemePreview from "@/components/Themes/ThemePreview.vue";
import ThemeSwatch from "@/components/Themes/ThemeSwatch.vue";
import {
  BUILT_IN_THEMES,
  MAX_CLINIC_THEMES,
  THEME_STORAGE_KEY,
  activeTheme,
  applyTheme,
  readableTextColorOn,
  dispatchThemeChanged,
  hydrateClinicTheme,
  isClinicThemeId,
  readStoredTheme,
  rgbToOpaqueHex,
  syncStoredClinicTheme,
  toRgb,
  unsetCustomTheme,
} from "@/utils/theme";
import { lockPageScroll, unlockPageScroll } from "@/utils/scrollLock";

const CHART_REDRAW_DEBOUNCE_MS = 100;
const CHART_REDRAW_MAX_WAIT_MS = 250;

export default {
  name: "ThemeModal",
  components: {
    ColorPicker,
    ThemePreview,
    ThemeSwatch,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  data() {
    // Copied: data() makes these reactive, and a write through the proxy would reach the shared constant.
    const colors = BUILT_IN_THEMES.map((theme) => ({ ...theme }));

    const storedTheme = readStoredTheme();
    const storedIsCustom = !!storedTheme && storedTheme.id === "custom";
    const customTheme = storedIsCustom
      ? { ...unsetCustomTheme(colors[0].themeColor), ...storedTheme }
      : unsetCustomTheme(colors[0].themeColor);

    return {
      colors,
      customTheme,
      customColorPicked: storedIsCustom,
      selectedTheme: storedIsCustom
        ? customTheme
        : activeTheme(this.$store.state.settings.themeSettings.data),
      step: "grid",
      pickerColor: { red: 0, green: 0, blue: 0, alpha: 1 },
      themeBeforePicker: null,
      pageScrollLocked: false,
      clinicThemeName: "",
      clinicThemeError: "",
    };
  },
  created() {
    this.redrawChartsDebounced = debounce(
      () => dispatchThemeChanged(this.selectedTheme.themeColor),
      CHART_REDRAW_DEBOUNCE_MS,
      { maxWait: CHART_REDRAW_MAX_WAIT_MS }
    );
  },
  beforeUnmount() {
    this.redrawChartsDebounced.cancel();
    this.releasePageScroll();
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.getThemeSettings().then((settings) => {
          if (settings) {
            syncStoredClinicTheme(settings);
          }
          const stored = readStoredTheme();
          if (this.isStoredVersionNewer(stored)) {
            this.updateSelectedTheme(stored);
            return;
          }
          const active = activeTheme(this.themeSettings);
          if (!stored && this.selectedTheme.id !== active.id) {
            this.updateSelectedTheme(active);
          }
        });
        return;
      }
      this.revertToStoredTheme();
    },
    clinicThemeName() {
      this.clinicThemeError = "";
    },
  },
  computed: {
    ...mapState({
      themeSettings: (state) => state.settings.themeSettings.data,
      savingClinicTheme: (state) => state.settings.themeSettings.saving,
      user: (state) => state.auth.user,
    }),
    isAdmin() {
      return !!this.user?.isAdmin;
    },
    clinicThemesFull() {
      return (this.themeSettings.themes || []).length >= MAX_CLINIC_THEMES;
    },
    canApply() {
      return this.step !== "picker" || this.customColorPicked;
    },
    canSaveAsClinicTheme() {
      return (
        this.customColorPicked &&
        !!this.clinicThemeName.trim() &&
        !this.savingClinicTheme
      );
    },
    show: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    clinicThemes() {
      return (this.themeSettings.themes || []).map(hydrateClinicTheme);
    },
    availableColors() {
      return [...this.colors, ...this.clinicThemes, this.customTheme];
    },
  },
  mounted() {
    this.updateSystemUI(this.selectedTheme);
  },
  methods: {
    ...mapActions({
      getThemeSettings: "settings/getThemeSettings",
      addTheme: "settings/addTheme",
    }),
    isStoredVersionNewer(stored) {
      if (!stored || !isClinicThemeId(stored.id)) return false;
      if (stored.id !== this.selectedTheme.id) return false;
      return (
        stored.themeColor !== this.selectedTheme.themeColor ||
        stored.name !== this.selectedTheme.name
      );
    },
    isUnsetCustom(item) {
      return item.id === "custom" && !this.customColorPicked;
    },
    themeLabel(item) {
      if (item.id === "custom") return "Custom color";
      return item.name || `Theme ${item.id}`;
    },
    isSelected(item) {
      if (this.isUnsetCustom(item)) return false;
      return item.id == this.selectedTheme.id;
    },
    closeModal() {
      this.$emit("update:modelValue", false);
    },
    selectTheme(item) {
      if (item.id === "custom") {
        this.openPicker();
        return;
      }
      this.updateSelectedTheme(item);
    },
    lockPageForPicker() {
      if (this.pageScrollLocked) return;
      this.pageScrollLocked = lockPageScroll();
    },
    releasePageScroll() {
      if (!this.pageScrollLocked) return;
      unlockPageScroll();
      this.pageScrollLocked = false;
    },
    openPicker() {
      this.lockPageForPicker();
      this.themeBeforePicker = this.selectedTheme;
      const startColor = this.customColorPicked
        ? this.customTheme.themeColor
        : this.selectedTheme.themeColor;
      this.pickerColor = toRgb(startColor);
      if (!this.customColorPicked) {
        this.customTheme.themeColor = rgbToOpaqueHex(this.pickerColor);
        this.customTheme.btnColor = readableTextColorOn(
          this.customTheme.themeColor
        );
      }
      this.updateSelectedTheme(this.customTheme);
      this.step = "picker";
    },
    backToThemes() {
      if (!this.customColorPicked && this.themeBeforePicker) {
        this.updateSelectedTheme(this.themeBeforePicker);
      }
      this.themeBeforePicker = null;
      this.step = "grid";
    },
    onPickerChange(color) {
      const hex = rgbToOpaqueHex(color);
      this.customTheme.themeColor = hex;
      this.customTheme.btnColor = readableTextColorOn(hex);
      this.customColorPicked = true;
      this.updateSelectedTheme(this.customTheme, false);
      this.redrawChartsDebounced();
    },
    updateSystemUI(item, notify = true) {
      applyTheme(item);
      if (notify) {
        this.redrawChartsDebounced.cancel();
        dispatchThemeChanged(item.themeColor);
      }
    },
    updateSelectedTheme(item, notify = true) {
      this.selectedTheme = item;
      this.updateSystemUI(item, notify);
    },
    revertToStoredTheme() {
      this.redrawChartsDebounced.cancel();
      const storedTheme = readStoredTheme();
      const storedIsCustom = !!storedTheme && storedTheme.id === "custom";
      Object.assign(
        this.customTheme,
        storedIsCustom
          ? { ...unsetCustomTheme(this.colors[0].themeColor), ...storedTheme }
          : unsetCustomTheme(this.colors[0].themeColor)
      );
      this.customColorPicked = storedIsCustom;
      this.themeBeforePicker = null;
      this.clinicThemeName = "";
      this.clinicThemeError = "";
      this.step = "grid";
      this.updateSelectedTheme(
        storedIsCustom ? this.customTheme : activeTheme(this.themeSettings)
      );
      this.releasePageScroll();
    },
    storeSelectedTheme() {
      localStorage.setItem(
        THEME_STORAGE_KEY,
        JSON.stringify(this.selectedTheme)
      );
      this.step = "grid";
      this.$emit("update:modelValue", false);
    },
    async saveAsClinicTheme() {
      if (!this.canSaveAsClinicTheme) return;
      let theme;
      try {
        theme = await this.addTheme({
          name: this.clinicThemeName.trim(),
          themeColor: this.customTheme.themeColor,
          silent: true,
        });
      } catch (err) {
        if (!err?.accessDenied) {
          this.clinicThemeError =
            err?.data?.error?.message ||
            err?.data?.message ||
            "Something went wrong...";
        }
        return;
      }
      this.updateSelectedTheme(hydrateClinicTheme(theme));
      this.storeSelectedTheme();
      Swal.fire({
        title: "Theme Added",
        text: `"${theme.name}" is now available in everyone's theme picker.`,
        icon: "success",
      });
    },
    dragMouseDown(event) {
      const tag = event.target.tagName;
      if (["INPUT", "TEXTAREA", "SELECT", "BUTTON", "A"].includes(tag)) return;

      event.preventDefault();

      const modal = this.$refs.draggableContainer.parentElement;
      const rect = modal.getBoundingClientRect();

      const startX = event.clientX;
      const startY = event.clientY;
      const startLeft = rect.left;
      const startTop = rect.top;

      // Pinned on the first move, not on mousedown: a swatch click is a mousedown too.
      function onMouseMove(event) {
        if (modal.style.position !== "fixed") {
          modal.style.position = "fixed";
          modal.style.margin = "0";
        }
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        modal.style.left = startLeft + dx + "px";
        modal.style.top = startTop + dy + "px";
      }

      window.addEventListener("mousemove", onMouseMove);

      window.onmouseup = function () {
        window.removeEventListener("mousemove", onMouseMove);
        window.onmouseup = null;
      };
    },
  },
};
</script>

<style lang="scss">
.theme-modal-content {
  display: flex;
  align-items: stretch;
  justify-content: center;
  background: white;
  border-radius: 24px;
  max-width: 90vw;
  max-height: 90vh;
  width: 600px;
  padding: 0;
}

.theme-modal-container {
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  cursor: move;

  .content-container {
    overflow-y: auto;
    overflow-x: auto;
    flex: 1;
    min-height: 0;
    position: relative;
  }

  .modal-title {
    text-align: center;
    font-size: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 34px;

    .close {
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      color: black;
      font-size: 35px;
      background: transparent;
      border: none;
      cursor: pointer;
    }
  }

  .colors-grid {
    column-gap: 3rem;
  }

  .colors-item {
    border-radius: 8px;
    margin-bottom: 12px;
    cursor: pointer;
    flex-direction: column;
    gap: 6px;

    &.selected {
      box-shadow: inset 0px 0px 6px #862f2f;
    }

    &:focus-visible {
      outline: 2px solid #2c7be5;
      outline-offset: 2px;
    }
  }

  .custom-caption {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    line-height: 1;
    color: #6e84a3;
  }

  .theme-caption {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    max-width: 88px;
    text-align: center;
    font-size: 12px;
    line-height: 1.25;
    color: #6e84a3;
    overflow-wrap: break-word;
  }

  .picker-step {
    cursor: default;
    padding-left: 1.5rem;
    padding-right: 1.5rem;

    .back-link {
      color: #2c7be5;
      text-decoration: none;
      font-size: 15px;
    }

    .manage-link {
      color: #2c7be5;
    }

    .clinic-theme-save-row {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      input {
        flex: 1 1 200px;
        min-width: 0;
      }

      .btn {
        flex: none;
        white-space: nowrap;
      }
    }

    .picker-holder {
      width: 296px;
      max-width: 100%;
      flex: none;

      // Hides alpha: the package has no prop for it, and the last input wrapper is Alpha.
      .alpha,
      .input-group > div:last-child {
        display: none;
      }
    }

    .preview-holder {
      flex: 1;
      min-width: 0;
    }

    @media (max-width: 575.98px) {
      padding-left: 0.5rem;
      padding-right: 0.5rem;
    }
  }
}
</style>
